interface ComparisonRowProps {
  capability: string;
  generic: { text: string; status: 'no' | 'partial' | 'yes' };
  ark: { text: string; status: 'no' | 'partial' | 'yes' };
}

const StatusIndicator: React.FC<{ status: 'no' | 'partial' | 'yes' }> = ({ status }) => {
  if (status === 'yes') {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 mr-2">
        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 mr-2">
        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 mr-2">
      <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
      </svg>
    </span>
  );
};

const ComparisonRow: React.FC<ComparisonRowProps> = ({ capability, generic, ark }) => (
  <tr className="border-t border-slate-100">
    <td className="px-6 py-5 text-slate-900 font-medium">{capability}</td>
    <td className="px-6 py-5 text-slate-500">
      <div className="flex items-center">
        <StatusIndicator status={generic.status} />
        <span>{generic.text}</span>
      </div>
    </td>
    <td className="px-6 py-5 text-slate-900 bg-violet-50/50">
      <div className="flex items-center">
        <StatusIndicator status={ark.status} />
        <span className="font-medium">{ark.text}</span>
      </div>
    </td>
  </tr>
);

export const CompetitiveSection: React.FC = () => {
  const comparisons: ComparisonRowProps[] = [
    {
      capability: "Construction terminology",
      generic: { text: "Limited understanding", status: 'no' },
      ark: { text: "Native fluency in specs, RFIs, submittals", status: 'yes' }
    },
    {
      capability: "2D drawings & 3D BIM",
      generic: { text: "Not supported", status: 'no' },
      ark: { text: "Full parsing and search", status: 'yes' }
    },
    {
      capability: "Procore & Autodesk integration",
      generic: { text: "Basic or none", status: 'partial' },
      ark: { text: "Deep, native connectors", status: 'yes' }
    },
    {
      capability: "Submittal compliance checking",
      generic: { text: "Not available", status: 'no' },
      ark: { text: "Automated review & flagging", status: 'yes' }
    },
    {
      capability: "Schedule (P6/MSP) understanding",
      generic: { text: "Not supported", status: 'no' },
      ark: { text: "Timeline-aware answers", status: 'yes' }
    },
    {
      capability: "Field-ready design",
      generic: { text: "Office-centric", status: 'no' },
      ark: { text: "Mobile-first for site teams", status: 'yes' }
    },
    {
      capability: "Enterprise security",
      generic: { text: "Varies by vendor", status: 'partial' },
      ark: { text: "RBAC, encryption, audit trails", status: 'yes' }
    }
  ];

  return (
    <section
      id="competitive"
      className="competitive-section section-spacing bg-slate-50"
    >
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
            Why ARK Beats Generic AI for Construction
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            General-purpose AI tools weren't built for RFIs, BIM models or submittals.
            ARK understands construction data natively and only answers when it has
            evidence from your project.
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600 uppercase tracking-wider">
                  Capability
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600 uppercase tracking-wider">
                  Generic AI
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-violet-700 uppercase tracking-wider bg-violet-50/50">
                  Ageiro ARK
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {comparisons.map((row) => (
                <ComparisonRow key={row.capability} {...row} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-600 text-base">
            <span className="font-semibold text-slate-800">No hallucinations.</span>{' '}
            Every ARK response is grounded in your project documents with full citations.
          </p>
        </div>
      </div>
    </section>
  );
};
